import { create } from 'zustand';

const UPGRADE_MODAL_TITLE = "Upgrade to get more features, credits and unlimited downloads";

// The quality-rating prompt shows on every generation, unless the user has opted
// out via "Don't show this again". The opt-out is stored in localStorage as an
// expiry timestamp so we eventually re-ask (90 days) rather than suppressing forever.
const QUALITY_RATING_OPTOUT_KEY = "l2it_quality_rating_optout_until";
const QUALITY_RATING_OPTOUT_MS = 90 * 24 * 60 * 60 * 1000;

type SideBarPageType = "MENU" | "INTEGRATIONS";

// Request to show the shared "rate the generated audio" prompt. Any feature
// (studio build/export, dubbing, voice cloning, …) fires this on completion.
// `tool` selects the default copy (see constants/qualityRatingCopy); `copy`
// overrides it per-call. org/integration/content ids fall back to the ones this
// store already tracks when omitted. `meta` carries extra analytics props.
export interface QualityRatingRequest {
    tool: string
    source: string
    orgId?: string
    integrationId?: string
    contentId?: string
    meta?: Record<string, any>
    copy?: { title?: string }
    // Viewport rect of the control that triggered the action (a
    // getBoundingClientRect snapshot). When present, the prompt is placed next to
    // it (tooltip-style, flipped/clamped to stay on screen); otherwise it falls
    // back to bottom-center.
    anchor?: { top: number; left: number; width: number; height: number } | null
}

type QualityRatingPromptState = QualityRatingRequest & { open: boolean; meta: Record<string, any>; id: number }

interface IGlobalStore {
    orgId: string
    integrationId: string
    contentId: string
    showUpgradeModal: boolean
    modalTitle: string
    modalSource: string | null
    isManualTrigger: boolean
    sideBarPageType: SideBarPageType
    showWhatsNewPanel: boolean
    showCreateProjectModal: boolean
    qualityRatingPrompt: QualityRatingPromptState
    requestQualityRating: (request: QualityRatingRequest) => void
    dismissQualityRating: () => void
    optOutQualityRating: () => void
    setOrgId: (orgId: string) => void
    setIntegrationId: (integrationId: string) => void
    setContentId: (contentId: string) => void
    toggleUpgradeModal: (value: boolean, options?: any) => void
    setSideBarPageType: (pageType: SideBarPageType) => void
    toggleWhatsNewPanel: (value: boolean) => void
    toggleCreateProjectModal: (value: boolean) => void
}

export const useGlobalStore = create<IGlobalStore>()((set) => ({
    orgId: '',
    integrationId: '',
    contentId: '',
    showUpgradeModal: false,
    modalTitle: UPGRADE_MODAL_TITLE,
    modalSource: null,
    isManualTrigger: false,
    sideBarPageType: "MENU",
    showWhatsNewPanel: false,
    showCreateProjectModal: false,
    qualityRatingPrompt: { open: false, tool: '', source: '', meta: {}, id: 0 },
    requestQualityRating: (request) => {
        // Suppress only if the user opted out and the opt-out hasn't expired.
        // Guarded so private mode / blocked storage can't break it (on error we show).
        try {
            const until = Number(localStorage.getItem(QUALITY_RATING_OPTOUT_KEY));
            if (until && Date.now() < until) return;
        } catch (_) { /* storage unavailable — fall through and show */ }
        set(() => ({
            qualityRatingPrompt: { meta: {}, ...request, open: true, id: Date.now() },
        }));
    },
    dismissQualityRating: () => set((state) => ({
        qualityRatingPrompt: { ...state.qualityRatingPrompt, open: false },
    })),
    optOutQualityRating: () => {
        // "Don't show this again" — persist an expiry so we re-ask after 90 days.
        try {
            localStorage.setItem(QUALITY_RATING_OPTOUT_KEY, String(Date.now() + QUALITY_RATING_OPTOUT_MS));
        } catch (_) { /* ignore write failures */ }
        set((state) => ({
            qualityRatingPrompt: { ...state.qualityRatingPrompt, open: false },
        }));
    },
    setOrgId: (orgId) => set(() => ({ orgId })),
    setIntegrationId: (integrationId) => set(() => ({ integrationId })),
    setContentId: (contentId) => set(() => ({ contentId })),
    toggleUpgradeModal: (show, options) => set((state) => {
        let modalTitle = UPGRADE_MODAL_TITLE;
        let modalSource = null;
        let isManualTrigger = false;
        
        if (options) {
            if (options.title) {
                modalTitle = options.title;
            }
            if (options.source) {
                modalSource = options.source;
            }
            if (options.isManual !== undefined) {
                isManualTrigger = options.isManual;
            }
        }
        
        return ({ 
            showUpgradeModal: show, 
            modalTitle: modalTitle,
            modalSource: modalSource,
            isManualTrigger: isManualTrigger
        })
    }),
    setSideBarPageType: (pageType) => set(() => ({ sideBarPageType: pageType })),
    toggleWhatsNewPanel: (value) => set(() => ({ showWhatsNewPanel: value })),
    toggleCreateProjectModal: (value) => set(() => ({ showCreateProjectModal: value })),
}))